import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Drawer, IconButton, List, ListItemButton, Stack, Typography} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {toTestId} from '../../../../utility/testIdUtil';

// Maria, 19 October 2026: on a phone the rows of profile tabs took most of the screen, and parents did not see the way on
// to the other sections. A phone has one bar instead: the open section, its status and its place, with arrows to the
// sections either side. Tapping the bar lists every section with its status, and each section ends with a button to
// the next one. Wider screens keep the tabs.

const STATUS_COLOURS = {red: '#c62828', green: '#2e7d32'};

const Status = ({status, testId}) => {
    if (!status) return null;
    return (
        <Box
            component="span"
            data-testid={testId}
            sx={{color: STATUS_COLOURS[status.colour], fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.02em', whiteSpace: 'nowrap'}}
        >
            {status.text}
        </Box>
    );
};

Status.propTypes = {
    status: PropTypes.shape({text: PropTypes.string, colour: PropTypes.oneOf(['red', 'green'])}),
    testId: PropTypes.string
};

export const PhoneSectionBar = ({tabs, index, statusOf, onSelect}) => {
    const [open, setOpen] = useState(false);
    const tab = tabs[index];
    if (!tab) return null;
    const choose = newIndex => {
        setOpen(false);
        onSelect(newIndex);
    };

    return (
        <>
            <Stack direction="row" alignItems="stretch" gap={0.5} sx={{width: '100%', py: 0.5}} data-testid="phone-section-bar">
                <IconButton
                    aria-label="Previous section"
                    disabled={index === 0}
                    onClick={() => onSelect(index - 1)}
                    sx={{border: '1px solid #c5d3e6', borderRadius: 2, backgroundColor: '#fff', width: 44}}
                    data-testid="phone-section-previous"
                >
                    <ChevronLeftIcon/>
                </IconButton>
                <Button
                    onClick={() => setOpen(true)}
                    aria-haspopup="dialog"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        color: '#1a1a1a',
                        backgroundColor: '#e4eefa',
                        border: '1px solid #c5d3e6',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        '&:hover': {backgroundColor: '#d8e6f7'}
                    }}
                    data-testid="phone-section-button"
                >
                    <Box sx={{textAlign: 'left', minWidth: 0}}>
                        <Typography sx={{fontSize: 16, fontWeight: 700, lineHeight: 1.3}} noWrap>{tab.label}</Typography>
                        <Stack direction="row" gap={1} alignItems="baseline">
                            <Typography component="span" sx={{fontSize: 12, color: '#4a5a6a'}}>{index + 1} of {tabs.length}</Typography>
                            <Status status={statusOf(tab)} testId="phone-section-status"/>
                        </Stack>
                    </Box>
                    <ExpandMoreIcon sx={{color: '#186BBE'}}/>
                </Button>
                <IconButton
                    aria-label="Next section"
                    disabled={index === tabs.length - 1}
                    onClick={() => onSelect(index + 1)}
                    sx={{border: '1px solid #c5d3e6', borderRadius: 2, backgroundColor: '#fff', width: 44}}
                    data-testid="phone-section-next"
                >
                    <ChevronRightIcon/>
                </IconButton>
            </Stack>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{sx: {borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh'}}}
                data-testid="phone-section-list"
            >
                <Box sx={{width: 40, height: 4, borderRadius: 2, backgroundColor: '#c5d3e6', mx: 'auto', mt: 1}}/>
                <List sx={{py: 1}}>
                    {tabs.map((each, eachIndex) => (
                        <ListItemButton
                            key={each.route}
                            selected={eachIndex === index}
                            onClick={() => choose(eachIndex)}
                            sx={{
                                py: 1.25,
                                px: 2.5,
                                justifyContent: 'space-between',
                                gap: 2,
                                borderLeft: eachIndex === index ? '3px solid #186BBE' : '3px solid transparent',
                                '&.Mui-selected': {backgroundColor: '#e4eefa'}
                            }}
                            data-testid={toTestId('phone-section', each.route)}
                        >
                            <Typography sx={{fontSize: 16, fontWeight: eachIndex === index ? 700 : 500}}>{each.label}</Typography>
                            <Status status={statusOf(each)}/>
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
        </>
    );
};

PhoneSectionBar.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, route: PropTypes.string})).isRequired,
    index: PropTypes.number.isRequired,
    statusOf: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired
};

// The button at the foot of a section that opens the next one.
export const PhoneNextSection = ({tabs, index, onSelect}) => {
    const next = tabs[index + 1];
    if (!next) return null;

    return (
        <Button
            variant="contained"
            fullWidth
            endIcon={<ChevronRightIcon/>}
            onClick={() => {
                onSelect(index + 1);
                window.scrollTo({top: 0});
            }}
            sx={{mt: 2, mb: 1, py: 1.25, textTransform: 'none', fontSize: 15, backgroundColor: '#186BBE', boxShadow: 'none'}}
            data-testid="phone-section-continue"
        >
            Next: {next.label}
        </Button>
    );
};

PhoneNextSection.propTypes = {
    tabs: PropTypes.array.isRequired,
    index: PropTypes.number.isRequired,
    onSelect: PropTypes.func.isRequired
};
